import { useEffect, useRef, useState } from "react";

/**
 * Generic full-bleed WebGL shader canvas (ogl). Always provides uTime /
 * uResolution / uMouse uniforms and a smoothed, window-tracked mouse so any
 * fragment shader can be cursor-reactive. Extra uniforms are passed verbatim.
 *
 * Setup: dynamic ogl import (SSR-safe), preserveDrawingBuffer + positive-z
 * layering to avoid the blank-at-scroll-0 compositing bug, prefers-reduced-motion
 * fallback, and full cleanup.
 */
type ShaderUniform = {
  value: number | number[] | number[][] | Float32Array | boolean;
};

type ShaderCanvasProps = {
  fragmentShader: string;
  uniforms?: Record<string, ShaderUniform>;
  /** Mouse-follow smoothing per frame (0–1). Lower = lazier trail. */
  smoothing?: number;
};

type CoreUniforms = {
  uTime: { value: number };
  uResolution: { value: [number, number, number] };
  uMouse: { value: Float32Array };
  iTime: { value: number };
  iResolution: { value: [number, number, number] };
};

const VERTEX_SHADER = `
attribute vec2 uv;
attribute vec2 position;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 0, 1);
}
`;

export function ShaderCanvas({
  fragmentShader,
  uniforms = {},
  smoothing = 0.06,
}: ShaderCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!containerRef.current) return;
    if (reduceMotion) return;
    const container = containerRef.current;

    let disposed = false;
    let cleanup: (() => void) | null = null;

    async function init() {
      const { Renderer, Program, Mesh, Triangle } = await import("ogl");
      if (disposed) return;

      const renderer = new Renderer({
        alpha: true,
        premultipliedAlpha: false,
        preserveDrawingBuffer: true,
      });
      const gl = renderer.gl;
      gl.clearColor(0, 0, 0, 0);

      const currentMouse: [number, number] = [0.5, 0.5];
      let targetMouse: [number, number] = [0.5, 0.5];

      function handleMouseMove(event: MouseEvent) {
        const rect = gl.canvas.getBoundingClientRect();
        targetMouse = [
          (event.clientX - rect.left) / rect.width,
          1.0 - (event.clientY - rect.top) / rect.height,
        ];
      }
      function handleMouseLeave() {
        targetMouse = [0.5, 0.5];
      }

      const geometry = new Triangle(gl);
      const program = new Program(gl, {
        vertex: VERTEX_SHADER,
        fragment: fragmentShader,
        uniforms: {
          uTime: { value: 0 },
          uResolution: {
            value: [
              gl.canvas.width,
              gl.canvas.height,
              gl.canvas.width / gl.canvas.height,
            ],
          },
          uMouse: { value: new Float32Array([0.5, 0.5]) },
          // Shadertoy-style aliases so react-bits / Shadertoy shaders drop in.
          iTime: { value: 0 },
          iResolution: {
            value: [
              gl.canvas.width,
              gl.canvas.height,
              gl.canvas.width / gl.canvas.height,
            ],
          },
          ...uniforms,
        },
      });

      const core = program.uniforms as unknown as CoreUniforms;

      function resize() {
        renderer.setSize(container.offsetWidth, container.offsetHeight);
        const res: [number, number, number] = [
          gl.canvas.width,
          gl.canvas.height,
          gl.canvas.width / gl.canvas.height,
        ];
        core.uResolution.value = res;
        core.iResolution.value = res;
      }
      window.addEventListener("resize", resize);
      resize();

      const mesh = new Mesh(gl, { geometry, program });
      container.appendChild(gl.canvas);
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseleave", handleMouseLeave);

      let animationFrameId: number | null = null;
      function render(time: number) {
        animationFrameId = requestAnimationFrame(render);
        core.uTime.value = time * 0.001;
        core.iTime.value = time * 0.001;
        currentMouse[0] += smoothing * (targetMouse[0] - currentMouse[0]);
        currentMouse[1] += smoothing * (targetMouse[1] - currentMouse[1]);
        core.uMouse.value[0] = currentMouse[0];
        core.uMouse.value[1] = currentMouse[1];
        renderer.render({ scene: mesh });
      }
      animationFrameId = requestAnimationFrame(render);

      cleanup = () => {
        if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
        window.removeEventListener("resize", resize);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseleave", handleMouseLeave);
        if (gl.canvas.parentNode === container) {
          container.removeChild(gl.canvas);
        }
        gl.getExtension("WEBGL_lose_context")?.loseContext();
      };
    }

    void init();
    return () => {
      disposed = true;
      cleanup?.();
    };
  }, [fragmentShader, uniforms, smoothing, reduceMotion]);

  if (reduceMotion) {
    return (
      <div
        ref={containerRef}
        className="h-full w-full bg-[radial-gradient(ellipse_at_center,rgba(47,143,78,0.06),transparent_60%)]"
        aria-hidden
      />
    );
  }
  return <div ref={containerRef} className="h-full w-full" />;
}
